// frontend/packages/business-console/src/components/uavs/PromptEngineMatrix.jsx
//
// The prompt x engine monitor: a grid of the prompts we ran (rows) against
// each AI engine (columns), with one pill per cell showing the strongest
// signal we recorded - Cited (the engine pointed at your own site) >
// Recommended > Mentioned > Absent. Untested pairs read as a faint dash.
//
// Reads insights.aiPresence.matrix straight from /v2/uavs/insights - no new
// fetch. Presentational only; the backend already pivoted the latest scan
// run. Horizontally scrollable on small screens so the grid never squeezes
// the labels.

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useTokens, Surface } from '../primitives';

// State -> { label, bg, border, fg } resolved off design tokens so the grid
// follows light/dark mode with everything else.
function stateStyles(t) {
  return {
    cited: {
      label: 'Cited',
      bg: t.purpleBg,
      border: t.purpleBorder,
      fg: t.purple,
    },
    recommended: {
      label: 'Recommended',
      bg: t.greenBg,
      border: t.greenBorder,
      fg: t.greenText,
    },
    mentioned: {
      label: 'Mentioned',
      bg: t.blueBg,
      border: t.blueBorder,
      fg: t.blue,
    },
    absent: { label: 'Absent', bg: t.grayBg, border: t.hairline, fg: t.muted },
    untested: {
      label: '-',
      bg: 'transparent',
      border: 'transparent',
      fg: t.faint,
    },
  };
}

const LEGEND_ORDER = ['cited', 'recommended', 'mentioned', 'absent'];

function Cell({ cell, styles }) {
  const s = styles[cell?.state] || styles.untested;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1,
        py: '3px',
        minWidth: 76,
        borderRadius: '7px',
        bgcolor: s.bg,
        border: '1px solid',
        borderColor: s.border,
        color: s.fg,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
      title={
        cell?.position != null &&
        cell?.state !== 'untested' &&
        cell?.state !== 'absent'
          ? `Position ${cell.position + 1}`
          : undefined
      }
    >
      {s.label}
    </Box>
  );
}

export default function PromptEngineMatrix({ matrix }) {
  const t = useTokens();
  const styles = stateStyles(t);
  if (!matrix || !matrix.prompts?.length || !matrix.engines?.length)
    return null;

  const { engines, prompts } = matrix;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{ fontSize: 15, fontWeight: 700, color: t.text, mb: 0.25 }}
      >
        Prompt x engine monitor
      </Typography>
      <Typography sx={{ fontSize: 13, color: t.textSecondary, mb: 1.5 }}>
        Exactly which prompts surfaced your business on each AI engine in the
        latest scan. &ldquo;Cited&rdquo; means the engine linked to your own
        site.
      </Typography>

      <Surface radius="14px" sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              minWidth: 360 + engines.length * 92,
              display: 'table',
              width: '100%',
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'table-row', bgcolor: t.tableHead }}>
              <Box
                sx={{
                  display: 'table-cell',
                  p: '10px 14px',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                  color: t.faint,
                  borderBottom: `1px solid ${t.hairline}`,
                  position: 'sticky',
                  left: 0,
                  bgcolor: t.tableHead,
                  zIndex: 1,
                }}
              >
                Prompt
              </Box>
              {engines.map((e) => (
                <Box
                  key={e.key}
                  sx={{
                    display: 'table-cell',
                    p: '10px 12px',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.4px',
                    color: t.faint,
                    textAlign: 'center',
                    borderBottom: `1px solid ${t.hairline}`,
                  }}
                >
                  {e.label}
                </Box>
              ))}
            </Box>

            {/* Rows */}
            {prompts.map((p, i) => (
              <Box
                key={`${p.text}-${i}`}
                sx={{
                  display: 'table-row',
                  '&:hover': { bgcolor: t.subtle },
                }}
              >
                <Box
                  sx={{
                    display: 'table-cell',
                    p: '10px 14px',
                    fontSize: 13,
                    color: t.text,
                    borderBottom:
                      i < prompts.length - 1
                        ? `1px solid ${t.hairline}`
                        : 'none',
                    verticalAlign: 'middle',
                    maxWidth: 320,
                    position: 'sticky',
                    left: 0,
                    bgcolor: t.card,
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, fontStyle: 'italic', color: t.text }}
                  >
                    &ldquo;{p.text}&rdquo;
                  </Typography>
                </Box>
                {engines.map((e) => (
                  <Box
                    key={e.key}
                    sx={{
                      display: 'table-cell',
                      p: '8px 12px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      borderBottom:
                        i < prompts.length - 1
                          ? `1px solid ${t.hairline}`
                          : 'none',
                    }}
                  >
                    <Cell cell={p.cells[e.key]} styles={styles} />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Surface>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.25 }}>
        {LEGEND_ORDER.map((key) => (
          <Box
            key={key}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}
          >
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '3px',
                bgcolor: styles[key].bg,
                border: `1px solid ${styles[key].border}`,
              }}
            />
            <Typography sx={{ fontSize: 11.5, color: t.textSecondary }}>
              {styles[key].label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
